/**
 * NeuroSpeak AI — device-only localStorage store.
 *
 * Non-diagnostic. All data lives in the user's browser. Nothing is sent
 * off-device unless the user explicitly triggers an export or share action.
 *
 * State shape:
 *   {
 *     profile: {
 *       consent_stored: boolean,
 *       language: "en" | "ta" | "hi",
 *       accessibility: { large_text, high_contrast, audio_cues, simple_ui },
 *     },
 *     baseline: {
 *       at: iso, results: {
 *         retrieval: { correct, total },
 *         recognition: { correct, total },
 *         sentence: { correct, total },
 *         functional: { correct, total },
 *       }
 *     } | null,
 *     level: 1..5,
 *     sessions: [{
 *       at, kind: "retrieval|recognition|sentence|functional|picture-word-sentence",
 *       word, target, response, cue_level: 0..4,
 *       correct: bool, reaction_ms
 *     }],
 *     vocab: { family: [{word,image?}], food, places, daily, personal },
 *     messages: [{ id, text, category }],
 *     feedback: [{ at, ease: "easy|comfortable|challenging|hard|unable", note? }],
 *   }
 */

const KEY = "silentscan_neurospeak_v1";

const DEFAULT_VOCAB = {
  family: [
    { word: "Mother" }, { word: "Father" }, { word: "Brother" },
    { word: "Sister" }, { word: "Child" },
  ],
  food: [
    { word: "Rice" }, { word: "Water" }, { word: "Tea" },
    { word: "Milk" }, { word: "Fruit" },
  ],
  places: [
    { word: "Home" }, { word: "Hospital" }, { word: "School" },
    { word: "Temple" }, { word: "Shop" },
  ],
  daily: [
    { word: "Phone" }, { word: "Chair" }, { word: "Bathroom" },
    { word: "Door" }, { word: "Clothes" },
  ],
  personal: [],
};

const DEFAULT_MESSAGES = [
  { id: "m1", text: "I need help.", category: "help" },
  { id: "m2", text: "I am tired.", category: "feelings" },
  { id: "m3", text: "I want water.", category: "food" },
  { id: "m4", text: "I want to go outside.", category: "daily" },
  { id: "m5", text: "I need to talk to my family.", category: "family" },
  { id: "m6", text: "I don't understand.", category: "communication" },
];

const DEFAULT = {
  profile: {
    consent_stored: false,
    language: "en",
    accessibility: {
      large_text: false,
      high_contrast: false,
      audio_cues: false,
      simple_ui: false,
    },
  },
  baseline: null,
  level: 1,
  sessions: [],
  vocab: DEFAULT_VOCAB,
  messages: DEFAULT_MESSAGES,
  feedback: [],
};

// -----------------------------------------------------------------------
// Persistence helpers
// -----------------------------------------------------------------------

export const loadSpeak = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT };
    const p = JSON.parse(raw);
    // Merge with defaults so newly added fields exist for old users.
    return {
      ...DEFAULT,
      ...p,
      profile: {
        ...DEFAULT.profile,
        ...(p.profile || {}),
        accessibility: {
          ...DEFAULT.profile.accessibility,
          ...((p.profile || {}).accessibility || {}),
        },
      },
      vocab: { ...DEFAULT.vocab, ...(p.vocab || {}) },
      messages: p.messages && p.messages.length ? p.messages : DEFAULT_MESSAGES,
    };
  } catch {
    return { ...DEFAULT };
  }
};

export const saveSpeak = (state) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch (e) {
    console.warn("NeuroSpeak persistence error:", e);
  }
};

export const clearSpeak = () => {
  localStorage.removeItem(KEY);
};

// -----------------------------------------------------------------------
// Recorders
// -----------------------------------------------------------------------

export const recordBaselineSpeak = (results) => {
  const s = loadSpeak();
  s.baseline = { at: new Date().toISOString(), results };
  // Bump level a little if the person did well on retrieval + recognition.
  const r = results.retrieval || { correct: 0, total: 1 };
  const g = results.recognition || { correct: 0, total: 1 };
  const overall = ((r.correct + g.correct) / Math.max(1, r.total + g.total));
  s.level = overall > 0.75 ? 3 : overall > 0.5 ? 2 : 1;
  saveSpeak(s);
  return s;
};

export const recordSpeakSession = (session) => {
  const s = loadSpeak();
  s.sessions = [
    ...(s.sessions || []).slice(-999),
    {
      at: new Date().toISOString(),
      kind: session.kind || "retrieval",
      word: session.word || null,
      target: session.target || null,
      response: session.response || null,
      cue_level: Number(session.cue_level || 0),
      correct: !!session.correct,
      reaction_ms: Number(session.reaction_ms || 0),
    },
  ];
  // Simple adaptive engine — recent 10 sessions.
  const recent = s.sessions.slice(-10);
  const independent = recent.filter((x) => x.correct && x.cue_level === 0).length;
  const cued = recent.filter((x) => x.correct && x.cue_level > 0).length;
  const failed = recent.filter((x) => !x.correct).length;
  if (independent >= 7) s.level = Math.min(5, (s.level || 1) + 1);
  else if (failed >= 6) s.level = Math.max(1, (s.level || 1) - 1);
  else if (cued >= 5) s.level = Math.max(1, s.level || 1); // hold
  saveSpeak(s);
  return s;
};

export const recordSpeakFeedback = (ease, note = "") => {
  const s = loadSpeak();
  s.feedback = [
    ...(s.feedback || []).slice(-499),
    { at: new Date().toISOString(), ease, note: String(note || "") },
  ];
  saveSpeak(s);
  return s;
};

// -----------------------------------------------------------------------
// Vocabulary + Messages CRUD
// -----------------------------------------------------------------------

export const addVocab = (category, word, image = null) => {
  const s = loadSpeak();
  if (!s.vocab[category]) s.vocab[category] = [];
  s.vocab[category] = [...s.vocab[category], { word, image }];
  saveSpeak(s);
  return s;
};

export const removeVocab = (category, word) => {
  const s = loadSpeak();
  if (!s.vocab[category]) return s;
  s.vocab[category] = s.vocab[category].filter((v) => v.word !== word);
  saveSpeak(s);
  return s;
};

export const addMessage = (text, category = "custom") => {
  const s = loadSpeak();
  const id = `m${Date.now()}`;
  s.messages = [...(s.messages || []), { id, text, category }];
  saveSpeak(s);
  return s;
};

export const removeMessage = (id) => {
  const s = loadSpeak();
  s.messages = (s.messages || []).filter((m) => m.id !== id);
  saveSpeak(s);
  return s;
};

export const updateAccessibility = (patch) => {
  const s = loadSpeak();
  s.profile = {
    ...s.profile,
    accessibility: { ...(s.profile?.accessibility || {}), ...patch },
  };
  saveSpeak(s);
  return s;
};

export const setLanguage = (lang) => {
  const s = loadSpeak();
  s.profile = { ...s.profile, language: lang };
  saveSpeak(s);
  return s;
};

// -----------------------------------------------------------------------
// Aggregates (non-diagnostic)
// -----------------------------------------------------------------------

const KINDS = ["retrieval", "recognition", "sentence", "functional", "picture-word-sentence"];

export const aggregateSpeak = (state) => {
  const s = state || loadSpeak();
  const sessions = s.sessions || [];
  const total = sessions.length;
  if (!total) {
    return {
      total_sessions: 0,
      words_practiced: 0,
      words_recognized: 0,
      naming_accuracy: 0,
      sentence_accuracy: 0,
      communication_attempts: 0,
      successful_responses: 0,
      cue_dependence: 0,
      streak: 0,
      level: s.level || 1,
    };
  }
  const uniqueWords = new Set(
    sessions.filter((x) => x.word).map((x) => x.word.toLowerCase())
  );
  const recognized = sessions.filter(
    (x) => (x.kind === "recognition" || x.kind === "retrieval") && x.correct
  );
  const naming = sessions.filter((x) => x.kind === "retrieval");
  const namingCorrect = naming.filter((x) => x.correct).length;
  const sent = sessions.filter((x) => x.kind === "sentence");
  const sentCorrect = sent.filter((x) => x.correct).length;
  const attempts = sessions.length;
  const successes = sessions.filter((x) => x.correct).length;
  const cueSum = sessions.reduce((a, x) => a + (x.cue_level || 0), 0);
  const cueMax = attempts * 4;
  const cueDep = cueMax ? Math.round((cueSum / cueMax) * 100) : 0;

  // Streak — number of consecutive days ending today with ≥1 session.
  const daySet = new Set(sessions.map((x) => (x.at || "").slice(0, 10)));
  let streak = 0;
  const cursor = new Date();
  while (streak < 90) {
    const key = cursor.toISOString().slice(0, 10);
    if (daySet.has(key)) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }

  return {
    total_sessions: total,
    words_practiced: uniqueWords.size,
    words_recognized: recognized.length,
    naming_accuracy: naming.length ? Math.round((namingCorrect / naming.length) * 100) : 0,
    sentence_accuracy: sent.length ? Math.round((sentCorrect / sent.length) * 100) : 0,
    communication_attempts: attempts,
    successful_responses: successes,
    cue_dependence: cueDep,
    streak,
    level: s.level || 1,
  };
};

// Cue distribution per session bucket (last N sessions).
export const cueDistribution = (state, windowSize = 10) => {
  const s = state || loadSpeak();
  const list = (s.sessions || []).slice(-windowSize);
  if (!list.length) return [
    { label: "Independent", value: 0, color: "#34d399" },
    { label: "Semantic hint", value: 0, color: "#67e8f9" },
    { label: "First letter", value: 0, color: "#a78bfa" },
    { label: "Multiple choice", value: 0, color: "#fbbf24" },
    { label: "Full model", value: 0, color: "#f87171" },
  ];
  const buckets = [0, 0, 0, 0, 0];
  list.forEach((x) => {
    const lvl = Math.min(4, Math.max(0, x.cue_level || 0));
    buckets[lvl] += 1;
  });
  const total = list.length || 1;
  return [
    { label: "Independent", value: Math.round((buckets[0] / total) * 100), color: "#34d399" },
    { label: "Semantic hint", value: Math.round((buckets[1] / total) * 100), color: "#67e8f9" },
    { label: "First letter", value: Math.round((buckets[2] / total) * 100), color: "#a78bfa" },
    { label: "Multiple choice", value: Math.round((buckets[3] / total) * 100), color: "#fbbf24" },
    { label: "Full model", value: Math.round((buckets[4] / total) * 100), color: "#f87171" },
  ];
};

// 14-day timeline for progress charts.
export const speakTimeline = (state, days = 14) => {
  const s = state || loadSpeak();
  const now = new Date();
  const out = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const list = (s.sessions || []).filter((x) => (x.at || "").slice(0, 10) === key);
    const naming = list.filter((x) => x.kind === "retrieval");
    const sent = list.filter((x) => x.kind === "sentence");
    const namingPct = naming.length
      ? Math.round((naming.filter((x) => x.correct).length / naming.length) * 100)
      : 0;
    const sentPct = sent.length
      ? Math.round((sent.filter((x) => x.correct).length / sent.length) * 100)
      : 0;
    const cueSum = list.reduce((a, x) => a + (x.cue_level || 0), 0);
    const cuePct = list.length ? Math.round((cueSum / (list.length * 4)) * 100) : 0;
    out.push({
      day: key.slice(5),
      naming: namingPct,
      sentence: sentPct,
      cue: cuePct,
      sessions: list.length,
    });
  }
  return out;
};

// Demo data for the 2-min demo / previews.
export const buildDemoSpeak = () => {
  const now = Date.now();
  const sessions = [];
  for (let d = 20; d >= 0; d -= 1) {
    const ts = new Date(now - d * 24 * 3600 * 1000);
    const iso = ts.toISOString();
    const trials = 3 + (d % 3);
    for (let t = 0; t < trials; t += 1) {
      const kind = t % 2 === 0 ? "retrieval" : t % 3 === 0 ? "sentence" : "recognition";
      const learning = Math.max(0, (20 - d) / 20);
      const cue = Math.max(0, Math.round(4 * (1 - learning) - Math.random()));
      const correct = Math.random() < (0.4 + learning * 0.55);
      sessions.push({
        at: iso,
        kind,
        word: ["Apple", "Water", "Chair", "Phone", "Mother", "Doctor"][Math.floor(Math.random() * 6)],
        target: null,
        response: null,
        cue_level: cue,
        correct,
        reaction_ms: 1500 - Math.round(learning * 500),
      });
    }
  }
  const feedback = [
    { at: new Date(now - 20 * 86400000).toISOString(), ease: "hard" },
    { at: new Date(now - 10 * 86400000).toISOString(), ease: "challenging" },
    { at: new Date(now - 2 * 86400000).toISOString(), ease: "comfortable" },
    { at: new Date(now).toISOString(), ease: "easy" },
  ];
  return {
    ...DEFAULT,
    baseline: {
      at: new Date(now - 25 * 86400000).toISOString(),
      results: {
        retrieval: { correct: 3, total: 10 },
        recognition: { correct: 6, total: 10 },
        sentence: { correct: 3, total: 8 },
        functional: { correct: 4, total: 8 },
      },
    },
    level: 3,
    sessions,
    feedback,
  };
};

// Quick language dictionary for a handful of headings so the language switcher
// gives real feedback in Chunk A. Full i18n comes in Chunk D.
export const L = (key, lang = "en") => {
  const dict = {
    "journey.title": {
      en: "My communication journey.",
      ta: "என் தொடர்பாடல் பயணம்.",
      hi: "मेरी संचार यात्रा।",
    },
    "journey.subtitle": {
      en: "Words. Sentences. Confidence.",
      ta: "வார்த்தைகள். வாக்கியங்கள். நம்பிக்கை.",
      hi: "शब्द। वाक्य। आत्मविश्वास।",
    },
    "safety.banner": {
      en: "NeuroSpeak AI is an assistive communication and rehabilitation-support prototype. It does not diagnose or treat aphasia and does not replace a qualified speech-language pathologist or healthcare professional.",
      ta: "NeuroSpeak AI ஒரு உதவி தொடர்பாடல் மற்றும் மறுவாழ்வு ஆதரவு முன்மாதிரி. இது அஃபேசியாவை கண்டறியவும் சிகிச்சை அளிக்கவும் செய்யாது, மேலும் ஒரு தகுதிவாய்ந்த பேச்சு-மொழி நிபுணரை மாற்றாது.",
      hi: "NeuroSpeak AI एक सहायक संचार और पुनर्वास सहायता प्रोटोटाइप है। यह वाणी दोष का निदान या उपचार नहीं करता और किसी योग्य वाणी-भाषा विशेषज्ञ का स्थान नहीं ले सकता।",
    },
  };
  return dict[key]?.[lang] || dict[key]?.en || key;
};
